// components/TicTacToe.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  alpha,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Badge,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Circle as CircleIcon,
  Close as XIcon,
  TableRestaurant as TableIcon,
  Send as SendIcon,
  People as PeopleIcon,
  SportsEsports as GameIcon,
  Computer as ComputerIcon,
  Wifi as WifiIcon,
  Speed as SpeedIcon,
  SmartToy as BotIcon,
  SportsEsports as SportsEsportsIcon
} from '@mui/icons-material';
import { useChat } from '../context/ChatContext';
import { useAuth } from '../context/AuthContext';

const TicTacToe = ({ open, onClose, language, currentTableId, currentTableName, startGameMod = 'singleplayer' }) => {
  const { 
    isConnected,
    onlinePlayers,
    pendingGameInvitation,
    registerGameEventHandlers,
    unregisterGameEventHandlers,
    sendGameInvitation,
    acceptGameInvitation,
    rejectGameInvitation,
    sendGameMove,
    requestGameRematch,
    getOnlinePlayers,
    setOnlinePlayers,
    registerPlayerReady,
    clearPendingInvitation
  } = useChat();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  
  // Game mode state
  const [gameMode, setGameMode] = useState(startGameMod);
  const [difficulty, setDifficulty] = useState('medium');
  
  // Game state
  const [gameId, setGameId] = useState(null);
  const [playerSymbol, setPlayerSymbol] = useState('X');
  const [board, setBoard] = useState(Array(9).fill(null));
  const [currentTurn, setCurrentTurn] = useState('X');
  const [winner, setWinner] = useState(null);
  const [winningLine, setWinningLine] = useState([]);
  const [opponent, setOpponent] = useState(null);
  const [gameStatus, setGameStatus] = useState('waiting');
  
  // UI state
  const [selectedOpponent, setSelectedOpponent] = useState(null);
  const [invitationSent, setInvitationSent] = useState(false);
  const [rematchRequested, setRematchRequested] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isComputerThinking, setIsComputerThinking] = useState(false);

  // Winning combinations
  const winPatterns = [
    [0, 1, 2], [3, 4, 5], [6, 7, 8],
    [0, 3, 6], [1, 4, 7], [2, 5, 8],
    [0, 4, 8], [2, 4, 6]
  ];

  // Check winner function
  const checkWinner = useCallback((boardState) => {
    for (const pattern of winPatterns) {
      const [a, b, c] = pattern;
      if (boardState[a] && boardState[a] === boardState[b] && boardState[a] === boardState[c]) {
        return { winner: boardState[a], line: pattern };
      }
    }
    if (boardState.every(cell => cell !== null)) {
      return { winner: 'tie', line: [] };
    }
    return { winner: null, line: [] };
  }, []);

  // Computer move logic
  const getComputerMove = useCallback((currentBoard) => {
    const availableMoves = currentBoard.reduce((moves, cell, index) => {
      if (cell === null) moves.push(index);
      return moves;
    }, []);

    if (availableMoves.length === 0) return null;

    if (difficulty === 'easy') {
      return availableMoves[Math.floor(Math.random() * availableMoves.length)];
    } 
    else if (difficulty === 'medium') {
      const isSmart = Math.random() < 0.7;
      if (isSmart) {
        return getSmartMove(currentBoard, availableMoves);
      }
      return availableMoves[Math.floor(Math.random() * availableMoves.length)];
    }
    else {
      return getSmartMove(currentBoard, availableMoves);
    }
  }, [difficulty]);

  const getSmartMove = useCallback((currentBoard, availableMoves) => {
    // Try to win (computer is O)
    for (const move of availableMoves) {
      const testBoard = [...currentBoard];
      testBoard[move] = 'O';
      const { winner: testWinner } = checkWinner(testBoard);
      if (testWinner === 'O') return move;
    }

    // Block player from winning (player is X)
    for (const move of availableMoves) {
      const testBoard = [...currentBoard];
      testBoard[move] = 'X';
      const { winner: testWinner } = checkWinner(testBoard);
      if (testWinner === 'X') return move;
    }

    // Take center
    if (availableMoves.includes(4)) return 4;

    // Take corners
    const corners = [0, 2, 6, 8];
    const availableCorners = corners.filter(c => availableMoves.includes(c));
    if (availableCorners.length > 0) {
      return availableCorners[Math.floor(Math.random() * availableCorners.length)];
    }

    return availableMoves[0];
  }, [checkWinner]);

  // Singleplayer move function
  const makeMove = useCallback((position) => {
    if (board[position] !== null) return;
    if (winner) return;
    if (gameStatus !== 'active') return;
    if (currentTurn !== playerSymbol) return;
    
    const newBoard = [...board];
    newBoard[position] = currentTurn;
    setBoard(newBoard);
    
    const { winner: gameWinner, line } = checkWinner(newBoard);
    
    if (gameWinner) {
      setWinner(gameWinner);
      setWinningLine(line);
      setGameStatus('completed');
      let message = '';
      if (gameWinner === playerSymbol) {
        message = '🎉 You won! 🎉';
      } else if (gameWinner === 'tie') {
        message = "It's a tie! 🤝";
      }
      if (message) {
        setNotification({ message, severity: gameWinner === playerSymbol ? 'success' : 'info' });
        setTimeout(() => setNotification(null), 3000);
      }
    } else {
      setCurrentTurn('O');
    }
  }, [board, currentTurn, winner, gameStatus, playerSymbol, checkWinner]);

  // Computer move effect
  useEffect(() => {
    const makeComputerMove = async () => {
      if (gameMode === 'singleplayer' && 
          gameStatus === 'active' && 
          currentTurn === 'O' && 
          !winner && 
          !isComputerThinking) {
        
        setIsComputerThinking(true);
        
        const delayTime = difficulty === 'easy' ? 800 : difficulty === 'medium' ? 600 : 400;
        await new Promise(resolve => setTimeout(resolve, delayTime));
        
        const availableMoves = board.reduce((moves, cell, index) => {
          if (cell === null) moves.push(index);
          return moves;
        }, []);
        
        if (availableMoves.length > 0) {
          let computerMove;
          
          switch(difficulty) {
            case 'easy':
              computerMove = availableMoves[Math.floor(Math.random() * availableMoves.length)];
              break;
            case 'medium':
              if (Math.random() < 0.7) {
                computerMove = getSmartMove(board, availableMoves);
              } else {
                computerMove = availableMoves[Math.floor(Math.random() * availableMoves.length)];
              }
              break;
            case 'hard':
            default:
              computerMove = getSmartMove(board, availableMoves);
              break;
          }
          
          if (computerMove !== null && computerMove !== undefined) {
            const newBoard = [...board];
            newBoard[computerMove] = 'O';
            setBoard(newBoard);
            
            const { winner: gameWinner, line } = checkWinner(newBoard);
            
            if (gameWinner) {
              setWinner(gameWinner);
              setWinningLine(line);
              setGameStatus('completed');
              let message = '';
              if (gameWinner === playerSymbol) {
                message = '🎉 You won! 🎉';
              } else if (gameWinner === 'tie') {
                message = "It's a tie! 🤝";
              } else {
                message = '😢 You lost! 😢';
              }
             // setNotification({ message, severity: gameWinner === playerSymbol ? 'success' : gameWinner === 'tie' ? 'info' : 'error' });
              setTimeout(() => setNotification(null), 3000);
            } else {
              setCurrentTurn('X');
            }
          }
        }
        
        setIsComputerThinking(false);
      }
    };
    
    makeComputerMove();
  }, [gameMode, gameStatus, currentTurn, winner, board, isComputerThinking, difficulty, playerSymbol, checkWinner, getSmartMove]);

  // Register game event handlers
  useEffect(() => {
    registerGameEventHandlers({
      onGameStarted: (game) => {
        console.log('Game started via global socket:', game);
        setGameId(game.gameId);
        setPlayerSymbol(game.playerSymbol);
        setBoard(game.board);
        setCurrentTurn(game.currentTurn);
        setOpponent(game.opponent);
        setGameStatus('active');
        setWinner(null);
        setWinningLine([]);
        setInvitationSent(false);
        setSelectedOpponent(null);
        clearPendingInvitation();
        setNotification({ message: 'Game started! Make your move!', severity: 'success' });
        setTimeout(() => setNotification(null), 3000);
      },
      
      onGameStateUpdated: (update) => {
        console.log('Game state updated:', update);
        setBoard(update.board);
        setCurrentTurn(update.currentTurn);
      },
      
      onGameEnded: (result) => {
        console.log('Game ended:', result);
        setWinner(result.winner);
        setWinningLine(result.winningLine || []);
        setBoard(result.board);
        setGameStatus('completed');
        
        const isWinner = result.winner === playerSymbol;
        const isTie = result.winner === 'tie';
        
        setNotification({
          message: isWinner ? '🎉 You won! 🎉' : isTie ? "It's a tie! 🤝" : '😢 You lost! 😢',
          severity: isWinner ? 'success' : isTie ? 'info' : 'error'
        });
        setTimeout(() => setNotification(null), 3000);
      },
      
      onGameInvitation: (invitation) => {
        setNotification({
          message: `${invitation.fromName} invited you to play!`,
          severity: 'info',
          type: 'invitation',
          invitation
        });
      },
      
      onInvitationSent: (data) => {
        setInvitationSent(true);
        setNotification({
          message: `Invitation sent to table ${data.targetTableId}`,
          severity: 'success'
        });
        setTimeout(() => setNotification(null), 3000);
      },
      
      onInvitationRejected: (data) => {
        setInvitationSent(false);
        setNotification({
          message: `Player at table ${data.targetTableId} rejected your invitation`,
          severity: 'warning'
        });
        setTimeout(() => setNotification(null), 3000);
      },
      
      onRematchRequested: (data) => {
        setNotification({
          message: `${data.from} requested a rematch!`,
          severity: 'info',
          type: 'rematch'
        });
      },
      
      onRematchStarted: (data) => {
        console.log('Rematch started:', data);
        setBoard(data.board);
        setCurrentTurn(data.currentTurn);
        setWinner(null);
        setWinningLine([]);
        setGameStatus('active');
        setRematchRequested(false);
        setNotification({ message: 'Rematch started!', severity: 'success' });
        setTimeout(() => setNotification(null), 3000);
      },
      
      onPlayerDisconnected: (data) => {
        setGameStatus('completed');
        setNotification({
          message: data.message,
          severity: 'error'
        });
        setTimeout(() => setNotification(null), 3000);
      },
      
onOnlinePlayers: (players) => {
  // Get current user's table ID from localStorage or prop
  const currentUserTableId = currentTableId?.toString() || localStorage.getItem('tableId');
  const currentUserId = user?.id?.toString();
  
  // Filter out:
  // 1. Current player's table
  // 2. Players currently in game
  // 3. Current user (if you want to exclude yourself by user ID)
  const filteredPlayers = players.filter(p => {
    // Exclude current player's table
    if (p.tableId === currentUserTableId) return false;
    
    // Exclude players currently in game
    if (p.isInGame) return false;
    
    // Optional: Exclude current user by ID (if you have user-based filtering)
    if (currentUserId && p.userId === currentUserId) return false;
    
    // Optional: Exclude players who are offline or inactive
    if (p.status === 'offline') return false;
    
    return true;
  });
  
  // Sort filtered players (optional: show VIP tables first)
  const sortedPlayers = filteredPlayers.sort((a, b) => {
    // VIP tables first
    if (a.isVip && !b.isVip) return -1;
    if (!a.isVip && b.isVip) return 1;
    // Then by table number
    return (a.tableNumber || 0) - (b.tableNumber || 0);
  });
  
  // Update local onlinePlayers state
  setOnlinePlayers(sortedPlayers);
  
  // Optional: Update context if you have one
  // updateOnlinePlayers(sortedPlayers);
  
  // Optional: Store in localStorage for persistence
  // localStorage.setItem('onlinePlayers', JSON.stringify(sortedPlayers));
  
  console.log('Filtered online players:', sortedPlayers);
},
    });
    
    // Register player as ready when component mounts
    if (currentTableId && open) {
      registerPlayerReady(currentTableId, currentTableName || `Table ${currentTableId}`);
    }
    
    // Get online players
    getOnlinePlayers();
    
    return () => {
      unregisterGameEventHandlers();
    };
  }, [currentTableId, currentTableName, open]);

  // Handle invitation response
  const acceptInvitationHandler = () => {
    if (!pendingGameInvitation) return;
    
    acceptGameInvitation(
      pendingGameInvitation.gameId,
      pendingGameInvitation.fromTable,
      currentTableId,
      currentTableName || `Table ${currentTableId}`
    );
    setGameMode('multiplayer');
    clearPendingInvitation();
    setNotification(null);
  };

  const rejectInvitationHandler = () => {
    if (!pendingGameInvitation) return;
    
    rejectGameInvitation(pendingGameInvitation.gameId, currentTableId);
    clearPendingInvitation();
    setNotification(null);
  };

  // Send invitation
  const sendInvitationHandler = () => {
    if (!selectedOpponent) return;
    
    sendGameInvitation(
      selectedOpponent.tableId,
      currentTableId,
      currentTableName || `Table ${currentTableId}`
    );
  };

  // Make move in multiplayer
  const multiplayerMakeMove = (position) => {
    if (!isMyTurn || board[position] !== null) return;
    sendGameMove(gameId, position, `table_${currentTableId}`);
  };

  // Request rematch
  const requestRematchGame = () => {
    requestGameRematch(gameId, `table_${currentTableId}`);
    setRematchRequested(true);
    setNotification(null);
  };

  // Start singleplayer game
  const startSingleplayerGame = () => {
    console.log('Starting singleplayer game');
    setBoard(Array(9).fill(null));
    setCurrentTurn('X');
    setWinner(null);
    setWinningLine([]);
    setGameStatus('active');
    setPlayerSymbol('X');
    setIsComputerThinking(false);
  };

  const resetMultiplayerGame = () => {
    setGameId(null);
    setPlayerSymbol(null);
    setBoard(Array(9).fill(null));
    setCurrentTurn('X');
    setWinner(null);
    setWinningLine([]);
    setOpponent(null);
    setGameStatus('waiting');
    setInvitationSent(false);
    setRematchRequested(false);
    getOnlinePlayers();
  };

  const isMyTurn = gameStatus === 'active' && currentTurn === playerSymbol && !winner;

  // Render cell
  const renderCell = (index) => {
    const value = board[index];
    const isWinning = winningLine.includes(index);
    const isClickable = gameStatus === 'active' && 
                        ((gameMode === 'singleplayer' && currentTurn === playerSymbol && !winner) ||
                         (gameMode === 'multiplayer' && isMyTurn)) && 
                        !value;
    
    return (
      <Box
        onClick={() => {
          if (gameMode === 'singleplayer') {
            if (isClickable) makeMove(index);
          } else {
            if (isClickable) multiplayerMakeMove(index);
          }
        }}
        sx={{
          width: '100%',
          aspectRatio: '1 / 1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isWinning ? alpha(theme.palette.primary.main, 0.2) : '#1A1A1A',
          border: '2px solid #2D2D2D',
          borderRadius: '16px',
          cursor: isClickable ? 'pointer' : 'default',
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: isClickable ? alpha(theme.palette.primary.main, 0.1) : '#1A1A1A',
            transform: isClickable ? 'scale(1.02)' : 'none',
          }
        }}
      >
        {value === 'X' && <XIcon sx={{ fontSize: { xs: 40, sm: 60 }, color: '#FFD700' }} />}
        {value === 'O' && <CircleIcon sx={{ fontSize: { xs: 40, sm: 60 }, color: '#4CAF50' }} />}
      </Box>
    );
  };

  const getGameStatusText = () => {
    if (winner === playerSymbol) return 'You won! 🎉';
    if (winner === 'tie') return "It's a tie! 🤝";
    if (winner && winner !== playerSymbol) return 'You lost! 😢';
    if (gameStatus === 'active') {
      if (gameMode === 'singleplayer') {
        return currentTurn === playerSymbol ? 'Your turn!' : 'Computer is thinking...';
      }
      return isMyTurn ? 'Your turn!' : `${opponent?.name}'s turn...`;
    }
    return '';
  };

  return (
    <>
      {/* Notification Alert */}
      {notification && notification.type !== 'invitation' && (
        <Alert 
          severity={notification.severity}
          sx={{ 
            position: 'fixed', 
            bottom: 80, 
            left: '50%', 
            transform: 'translateX(-50%)',
            zIndex: 2000,
            backgroundColor: '#0D0D0D',
            border: `1px solid ${notification.severity === 'success' ? '#4CAF50' : notification.severity === 'error' ? '#FF3D00' : '#FFD700'}`,
            color: '#FFFFFF',
            minWidth: 250,
            textAlign: 'center'
          }}
        >
          {notification.message}
        </Alert>
      )}

      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            backgroundColor: '#0D0D0D',
            borderRadius: isMobile ? 0 : '24px',
            border: '2px solid #FFD700',
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid #2D2D2D',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #FFD700 0%, #FFC107 100%)',
          color: '#000000',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <GameIcon />
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              {language === 'ar' ? 'لعبة X O' : 'Tic Tac Toe'}
            </Typography>
            <Chip 
              label={isConnected ? 'Online' : 'Offline'}
              size="small"
              sx={{ backgroundColor: isConnected ? '#4CAF50' : '#FF3D00', color: '#FFF' }}
            />
          </Box>
          
          {/* Game Mode Toggle */}
          <ToggleButtonGroup
            value={gameMode}
            exclusive
            onChange={(e, newMode) => {
              if (newMode) {
                setGameMode(newMode);
                if (newMode === 'singleplayer') {
                  startSingleplayerGame();
                } else {
                  resetMultiplayerGame();
                }
              }
            }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                color: '#000000',
                borderColor: '#000000',
                '&.Mui-selected': {
                  backgroundColor: '#000000',
                  color: '#FFD700',
                }
              }
            }}
          >
            <ToggleButton value="singleplayer">
              <ComputerIcon sx={{ mr: 0.5 }} /> {language === 'ar' ? 'ضد الكمبيوتر' : 'VS Computer'}
            </ToggleButton>
            <ToggleButton value="multiplayer">
              <WifiIcon sx={{ mr: 0.5 }} /> {language === 'ar' ? 'متعدد اللاعبين' : 'Multiplayer'}
            </ToggleButton>
          </ToggleButtonGroup>
          
          <IconButton onClick={onClose} sx={{ color: '#000000' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Grid container spacing={3}>
            {/* Game Board */}
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {/* Game Status */}
                <Paper sx={{ 
                  p: 2, 
                  mb: 3, 
                  backgroundColor: '#1A1A1A',
                  borderRadius: '16px',
                  width: '100%',
                  textAlign: 'center'
                }}>
                  <Typography variant="h6" sx={{ color: gameStatus === 'active' && ((gameMode === 'singleplayer' && currentTurn === playerSymbol) || (gameMode === 'multiplayer' && isMyTurn)) ? '#FFD700' : '#B3B3B3' }}>
                    {getGameStatusText()}
                  </Typography>
                  {gameMode === 'singleplayer' && gameStatus === 'active' && isComputerThinking && (
                    <CircularProgress size={20} sx={{ color: '#FFD700', ml: 1 }} />
                  )}
                  {opponent && gameMode === 'multiplayer' && gameStatus === 'active' && (
                    <Typography variant="caption" sx={{ color: '#B3B3B3', display: 'block', mt: 1 }}>
                      Playing against: {opponent.name}
                    </Typography>
                  )}
                </Paper>

                {/* Difficulty Selector for Singleplayer */}
                {gameMode === 'singleplayer' && gameStatus === 'waiting' && (
                  <Paper sx={{ p: 2, mb: 3, width: '100%', backgroundColor: '#1A1A1A' }}>
                    <Typography variant="body2" sx={{ color: '#B3B3B3', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SpeedIcon sx={{ fontSize: 16 }} />
                      {language === 'ar' ? 'مستوى الصعوبة' : 'Difficulty Level'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {['easy', 'medium', 'hard'].map((level) => (
                        <Chip
                          key={level}
                          label={language === 'ar' 
                            ? level === 'easy' ? 'سهل' : level === 'medium' ? 'متوسط' : 'صعب'
                            : level}
                          onClick={() => setDifficulty(level)}
                          sx={{
                            backgroundColor: difficulty === level ? '#FFD700' : '#2D2D2D',
                            color: difficulty === level ? '#000000' : '#FFFFFF',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: '#FFD700',
                              color: '#000000',
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Paper>
                )}

                {/* Start Game Button for Singleplayer */}
                {gameMode === 'singleplayer' && gameStatus === 'waiting' && (
                  <Button
                    variant="contained"
                    onClick={startSingleplayerGame}
                    startIcon={<GameIcon />}
                    sx={{ mb: 3, backgroundColor: '#FFD700', color: '#000000' }}
                  >
                    {language === 'ar' ? 'بدء اللعب' : 'Start Game'}
                  </Button>
                )}

                {/* Game Grid */}
                {(gameStatus === 'active' || gameStatus === 'completed') && (
                  <Box sx={{ 
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 2,
                    width: '100%',
                    maxWidth: { xs: 350, sm: 450, md: 550 },
                    mx: 'auto'
                  }}>
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((index) => renderCell(index))}
                  </Box>
                )}

                {/* Game Controls */}
                {gameStatus === 'completed' && winner && (
                  <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    {gameMode === 'multiplayer' && (
                      <Button
                        variant="outlined"
                        onClick={requestRematchGame}
                        disabled={rematchRequested}
                        sx={{ borderColor: '#FFD700', color: '#FFD700' }}
                      >
                        {rematchRequested ? 'Waiting...' : (language === 'ar' ? 'طلب مباراة جديدة' : 'Request Rematch')}
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      onClick={() => {
                        if (gameMode === 'singleplayer') {
                          startSingleplayerGame();
                        } else {
                          resetMultiplayerGame();
                        }
                      }}
                      sx={{ backgroundColor: '#FFD700', color: '#000000' }}
                    >
                      {language === 'ar' ? 'العب مرة أخرى' : 'Play Again'}
                    </Button>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Players List / Info */}
            <Grid item xs={12} md={4}>
              {gameMode === 'multiplayer' ? (
                <Paper sx={{ 
                  p: 2, 
                  backgroundColor: '#1A1A1A', 
                  borderRadius: '16px',
                  border: '1px solid #2D2D2D'
                }}>
                  <Typography variant="h6" sx={{ color: '#FFD700', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WifiIcon /> {language === 'ar' ? 'اللاعبون المتصلون' : 'Online Players'}
                    {isConnected && (
                      <Chip label="Connected" size="small" sx={{ backgroundColor: '#4CAF50', color: '#FFF', ml: 1 }} />
                    )}
                  </Typography>
                  
                  {opponent && gameStatus !== 'waiting' ? (
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#0D0D0D', borderRadius: '12px' }}>
                      <Typography variant="subtitle2" sx={{ color: '#FFD700' }}>
                        {language === 'ar' ? 'الخصم الحالي' : 'Current Opponent'}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                        <Avatar sx={{ bgcolor: '#FFD700', color: '#000' }}>
                          <TableIcon />
                        </Avatar>
                        <Box>
                          <Typography sx={{ color: '#FFF' }}>{opponent.name}</Typography>
                          <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                            {playerSymbol === 'X' ? 'Plays O' : 'Plays X'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                      {!onlinePlayers || onlinePlayers.length === 0 ? (
                        <Typography sx={{ color: '#B3B3B3', textAlign: 'center', py: 4 }}>
                          {language === 'ar' ? 'لا يوجد لاعبون متصلون' : 'No players online'}
                        </Typography>
                      ) : (
                        onlinePlayers.map((player) => (
                          <ListItem
                            key={player.tableId}
                            sx={{
                              mb: 1,
                              backgroundColor: selectedOpponent?.tableId === player.tableId ? alpha('#FFD700', 0.1) : 'transparent',
                              borderRadius: '12px',
                              border: selectedOpponent?.tableId === player.tableId ? '1px solid #FFD700' : '1px solid #2D2D2D',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: alpha('#FFD700', 0.05),
                              }
                            }}
                            onClick={() => setSelectedOpponent(player)}
                          >
                            <ListItemAvatar>
                              <Badge
                                color="success"
                                variant="dot"
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                              >
                                <Avatar sx={{ bgcolor: '#2D2D2D' }}>
                                  <TableIcon />
                                </Avatar>
                              </Badge>
                            </ListItemAvatar>
                            <ListItemText
                              primary={`Table ${player.tableId}`}
                              secondary="Ready to play"
                              primaryTypographyProps={{ sx: { color: '#FFF' } }}
                              secondaryTypographyProps={{ sx: { color: '#4CAF50' } }}
                            />
                          </ListItem>
                        ))
                      )}
                    </List>
                  )}

{selectedOpponent && gameStatus === 'waiting' && !invitationSent && (
  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Button
      fullWidth
      variant="contained"
      onClick={sendInvitationHandler}
      startIcon={<SendIcon />}
      sx={{
        backgroundColor: '#FFD700',
        color: '#0D0D0D',
        py: 1.2,
        borderRadius: '12px',
        fontSize: '0.85rem',
        fontWeight: 700,
        textTransform: 'none',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: '#FFE55C',
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(255,215,0,0.3)'
        }
      }}
    >
      {language === 'ar' ? 'إرسال دعوة' : 'Send Invitation'}
    </Button>
    
    <Button
      disabled={true}
      fullWidth
      variant="outlined"
      startIcon={<SportsEsportsIcon />}
      sx={{
        borderColor: 'rgba(255,215,0,0.3)',
        color: '#888888',
        py: 1.2,
        borderRadius: '12px',
        fontSize: '0.8rem',
        fontWeight: 500,
        textTransform: 'none',
        cursor: 'not-allowed',
        '&.Mui-disabled': {
          backgroundColor: 'rgba(255,215,0,0.05)',
          borderColor: 'rgba(255,215,0,0.15)',
          color: '#555555'
        }
      }}
    >
      {language === 'ar' ? 'العب علي مشروب (قريباً)' : 'Play On Drink Invitation (Soon)'}
    </Button>
  </Box>
)}
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => getOnlinePlayers()}
                    startIcon={<RefreshIcon />}
                    sx={{ mt: 2, borderColor: '#2D2D2D', color: '#B3B3B3' }}
                  >
                    {language === 'ar' ? 'تحديث' : 'Refresh'}
                  </Button>
                </Paper>
              ) : (
                <Paper sx={{ 
                  p: 2, 
                  backgroundColor: '#1A1A1A', 
                  borderRadius: '16px',
                  border: '1px solid #2D2D2D'
                }}>
                  <Typography variant="h6" sx={{ color: '#FFD700', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BotIcon /> {language === 'ar' ? 'معلومات اللعبة' : 'Game Info'}
                  </Typography>
                  
                  <Box sx={{ p: 2, bgcolor: '#0D0D0D', borderRadius: '12px', mb: 2 }}>
                    <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                      {language === 'ar' 
                        ? 'أنت تلعب ضد الكمبيوتر. اختر مستوى الصعوبة المناسب لك.'
                        : 'You are playing against the computer. Choose your difficulty level.'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: '#0D0D0D', borderRadius: '12px' }}>
                    <Typography variant="body2" sx={{ color: '#FFF' }}>
                      {language === 'ar' ? 'مستوى الصعوبة' : 'Difficulty'}
                    </Typography>
                    <Chip 
                      label={difficulty === 'easy' ? (language === 'ar' ? 'سهل' : 'Easy') : difficulty === 'medium' ? (language === 'ar' ? 'متوسط' : 'Medium') : (language === 'ar' ? 'صعب' : 'Hard')}
                      sx={{ backgroundColor: '#FFD700', color: '#000', fontWeight: 'bold' }}
                    />
                  </Box>
                </Paper>
              )}

              {/* Game Tips */}
              <Paper sx={{ 
                mt: 2, 
                p: 2, 
                backgroundColor: alpha('#FFD700', 0.05),
                borderRadius: '16px',
                border: '1px solid #FFD700'
              }}>
                <Typography variant="caption" sx={{ color: '#B3B3B3', display: 'block', mb: 1 }}>
                  💡 {language === 'ar' ? 'كيف تلعب' : 'How to Play'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#B3B3B3', fontSize: '12px' }}>
                  {language === 'ar' 
                    ? '• اضغط على أي مربع فارغ لوضع علامتك\n• احصل على 3 علامات متتالية للفوز\n• الطاولات المشغولة لا يمكن اللعب عليها'
                    : '• Tap any empty square to place your mark\n• Get 3 marks in a row to win\n• Occupied squares cannot be played'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid #2D2D2D' }}>
          <Button onClick={onClose} sx={{ color: '#B3B3B3' }}>
            {language === 'ar' ? 'إغلاق' : 'Close'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TicTacToe;